import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Platform } from 'react-native';
import Modal from 'utils/packages/Modal';
import { getToken, signInV2, storeAccessAndRefreshToken, } from 'actions/signin';
import { getFeed } from 'actions/feed';
import useFetchOld from 'hooks/useFetch';
import { SIGN_IN_RESET } from 'actions/types';
import { storePasscode } from 'utils/passcode';
import { updateWealthAccounts } from 'actions/invest';
import { useAppDispatch, useAppStore } from 'store/hooks';
import { setPasscodeInKeychainSuccess } from 'actions/user';
import { getClientId, setClientId } from 'utils/jwtHelpers';
import { useLoginStackNavigation } from 'utils/types/navigationV6';
import { ThrowableFetchError } from 'hooks';
import { useLoadInitialData, } from './useLoadInitialData';
import useAppleSignIn from './useAppleSignIn';
import useGoogleSignIn from './useGoogleSignIn';
import useFacebookSignIn from './useFacebookSignIn';
import { useNavigateToNextScreen } from './useNavigateToNextScreen';
import { isWeb } from '../../constants';
export const ALT_STEP_LABELS = {
    pin_reset_email: 'Verify via email',
    pin_reset_email_otp: 'Verify via email',
    sms_otp: 'Verify via SMS',
    apple: 'Verify via Apple',
    facebook: 'Verify via Facebook',
    google: 'Verify via Google',
    phone_number: 'Verify via phone',
    pin: 'Verify via pin',
    token: '', // will never really happen
    load_initial_data: '', // client side only
    update_phone_email_otp: 'Verify via email',
};
const useSignIn = ({ reset, } = {}) => {
    const [, , , fetchSignIn] = useFetchOld({
        debug: false,
    });
    const [, , , fetchSignInThrows] = useFetchOld({
        debug: false,
        throws: true,
    });
    const [, , , fetchToken] = useFetchOld({ debug: false });
    const [isFetching, setIsFetching] = useState(null);
    const [googleSignIn] = useGoogleSignIn();
    const [facebookSignIn] = useFacebookSignIn();
    const [appleSignIn] = useAppleSignIn();
    const { loadInitialData } = useLoadInitialData();
    const dispatch = useAppDispatch();
    const navigation = useLoginStackNavigation();
    useEffect(() => {
        if (reset) {
            dispatch({
                type: SIGN_IN_RESET,
            });
        }
    }, [reset, dispatch]);
    const cachedClientId = useRef(undefined);
    const store = useAppStore();
    const navigateToNextScreen = useNavigateToNextScreen();
    const handleNextStep = useCallback(async (nextStep, params) => {
        switch (nextStep) {
            // None of the flows currently have facebook or apple as next or alternative step, but future-proofing
            case 'apple': {
                const result = await appleSignIn();
                if (result) {
                    const appleResult = await fetchSignIn(signInV2({
                        ...params,
                        apple: {
                            applicationType: Platform.OS === 'android' ? 'web' : 'ios',
                            authorizationCode: result.authorizationCode,
                        },
                    }));
                    if (appleResult && appleResult.nextStep !== 'apple') {
                        await handleNextStep(appleResult.nextStep, { ...params });
                    }
                }
                break;
            }
            case 'facebook': {
                const result = await facebookSignIn();
                if (result) {
                    const facebookResult = await fetchSignIn(signInV2({
                        ...params,
                        facebook: result,
                    }));
                    if (facebookResult && facebookResult.nextStep !== 'facebook') {
                        await handleNextStep(facebookResult.nextStep, { ...params });
                    }
                }
                break;
            }
            case 'google': {
                const result = await googleSignIn();
                if (result) {
                    const googleResult = await fetchSignIn(signInV2({
                        ...params,
                        google: {
                            token: result.token,
                        },
                    }));
                    if (googleResult && googleResult.nextStep !== 'google') {
                        await handleNextStep(googleResult.nextStep, { ...params });
                    }
                }
                break;
            }
            case 'phone_number':
                // forward to PhoneVerification
                navigation.replace('PhoneVerificationScreen', { flow: 'signIn' });
                break;
            case 'sms_otp':
                navigation.navigateDeprecated('PhoneVerificationConfirmScreen', {
                    flow: 'signIn',
                    phoneNumber: params?.phoneNumber?.phoneNumber,
                    prefix: params?.phoneNumber?.countryCode,
                });
                break;
            case 'pin':
                if (params.updatePhone && params.emailOtp) {
                    navigation.navigate('Success', {
                        subTitle: 'Your email is verified. Sign in now to get started!',
                        onContinue: () => {
                            navigation.navigateDeprecated('SignInPasscodeScreen', {
                                flow: 'sign-in',
                            });
                            return true;
                        },
                    });
                }
                else {
                    navigation.navigateDeprecated('SignInPasscodeScreen', {
                        flow: 'sign-in',
                    });
                }
                break;
            case 'pin_reset_email_otp':
                navigation.navigate('EmailVerification', {
                    flow: 'resetPin',
                    recipientEmail: params.pinResetEmail ?? '',
                });
                break;
            case 'update_phone_email_otp':
                navigation.navigate('EmailVerification', {
                    flow: 'updatePhone',
                    recipientEmail: params.updatePhone?.email ?? '',
                });
                break;
            case 'token': {
                // we’re ready to sign in, call token endpoint
                const tokenResponse = await fetchToken(getToken(params));
                if (tokenResponse) {
                    // sign in complete
                    await storeAccessAndRefreshToken(tokenResponse);
                    await handleNextStep('load_initial_data', { ...params });
                }
                break;
            }
            case 'load_initial_data': {
                const onSuccess = async ({ user }) => {
                    if (!isWeb) {
                        // no need for try catch because storePasscode doesnt throw
                        const result = await storePasscode(undefined, user.user.id);
                        if (result) {
                            /**
                             * There was no pin stored in the storePasscode generator
                             * we store the pin if it is present in params
                             *
                             * case of phone number -> pin -> sms code
                             */
                            if (user.user.pinLastChangedAt) {
                                dispatch(setPasscodeInKeychainSuccess(user.user.pinLastChangedAt));
                            }
                        }
                        else if (params.pin) {
                            /**
                             * There was no pin stored in the storePasscode generator
                             * we store the pin if it is present in params
                             *
                             * case of phone number -> sms code -> pin
                             */
                            const result = await storePasscode(params.pin, user.user.id.toString());
                            if (result && user.user.pinLastChangedAt) {
                                dispatch(setPasscodeInKeychainSuccess(user.user.pinLastChangedAt));
                            }
                        }
                        dispatch(updateWealthAccounts());
                    }
                    dispatch(getFeed(true));
                    navigateToNextScreen();
                };
                const onError = (e) => {
                    Modal.showError(e.message, () => {
                        signIn({
                            loadInitialData: true,
                            clientId: params.clientId,
                            pin: params.pin,
                        });
                    }, undefined, undefined, undefined);
                };
                await loadInitialData(onSuccess, onError);
                break;
            }
            default:
                Alert.alert('Error', 'No available login methods, please ensure your app is up to date.');
        }
    }, 
    // Need to avoid a loop here
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        facebookSignIn,
        googleSignIn,
        fetchToken,
        appleSignIn,
        fetchSignIn,
        loadInitialData,
    ]);
    const nextAlternativeStep = useCallback((exclude) => {
        const { currentState } = store.getState().signIn;
        if (currentState?.alternativeSteps &&
            currentState.alternativeSteps.length) {
            const steps = currentState.alternativeSteps.filter((step) => !exclude?.includes(step));
            return steps[0] || null;
        }
        return null;
    }, []);
    const alternativeStep = useCallback(async (fallback, excludeSteps) => {
        const { signInParameters: signInParamters, currentState } = store.getState().signIn;
        if (currentState && signInParamters) {
            const handleAlternativeStep = async (alternativeStep) => {
                if (alternativeStep === 'sms_otp') {
                    const sendResult = await fetchSignIn(signInV2({
                        ...signInParamters,
                        clientId: currentState.clientId,
                        sendSmsOtp: true,
                        sendPinResetEmail: false,
                    }));
                    if (sendResult) {
                        handleNextStep(sendResult?.nextStep, {
                            ...signInParamters,
                            clientId: currentState.clientId,
                        });
                    }
                }
                else if (alternativeStep === 'pin_reset_email') {
                    try {
                        const sendResult = await fetchSignInThrows(signInV2({
                            ...signInParamters,
                            clientId: currentState.clientId,
                            sendPinResetEmail: true,
                            sendSmsOtp: false,
                        }, true));
                        if (sendResult) {
                            handleNextStep(sendResult.nextStep, {
                                ...signInParamters,
                                pinResetEmail: sendResult.pinResetEmail,
                                clientId: currentState.clientId,
                            });
                        }
                    }
                    catch (error) {
                        if (error instanceof ThrowableFetchError &&
                            error.fetchError.apiPayload?.status === 409) {
                            handleNextStep('pin_reset_email_otp', {
                                ...signInParamters,
                                pinResetEmail: currentState.pinResetEmail,
                                clientId: currentState.clientId,
                            });
                        }
                        else {
                            Modal.showError(error instanceof Error
                                ? error.message
                                : 'Something went wrong');
                        }
                    }
                }
                else {
                    handleNextStep(alternativeStep, {
                        ...signInParamters,
                        clientId: currentState.clientId,
                    });
                }
            };
            if (currentState.alternativeSteps &&
                currentState.alternativeSteps.length) {
                const next = nextAlternativeStep(excludeSteps);
                if (next) {
                    return handleAlternativeStep(next);
                }
            }
            if (fallback) {
                // No alternative steps available use the fallback
                fallback();
            }
            else {
                navigation.navigateDeprecated('Support', {});
            }
        }
        return undefined;
    }, [fetchSignIn, fetchSignInThrows, handleNextStep, nextAlternativeStep]);
    const signIn = useCallback(async (params) => {
        if (reset) {
            // Since this is the 'start' of the flow we must reset the state
            dispatch({
                type: SIGN_IN_RESET,
            });
        }
        const { signInParameters: signInParamters } = store.getState().signIn;
        let clientId = cachedClientId.current;
        if (signInParamters && !clientId) {
            // If we’re half way through a sign in flow we should already have
            clientId = (await getClientId()) || undefined;
            cachedClientId.current = clientId;
        }
        if (params.apple) {
            setIsFetching('apple');
        }
        else if (params.facebook) {
            setIsFetching('facebook');
        }
        else if (params.google) {
            setIsFetching('google');
        }
        else if (params.pin) {
            setIsFetching('pin');
        }
        else if (params.smsOtp) {
            setIsFetching('sms_otp');
        }
        else if (params.loadInitialData) {
            setIsFetching('load_initial_data');
        }
        const postParams = reset
            ? params
            : {
                ...signInParamters,
                ...params,
                clientId,
            };
        try {
            if (params.loadInitialData) {
                await handleNextStep('load_initial_data', {
                    loadInitialData: true,
                    clientId,
                    pin: params.pin,
                });
                return false;
            }
            // For pin or smsOtp we want this to throw, so we can handle inline errors
            const result = await (params.pin || params.smsOtp
                ? fetchSignInThrows(signInV2(postParams, true))
                : fetchSignIn(signInV2(postParams)));
            if (!result) {
                return false;
            }
            if (result.clientId !== clientId) {
                clientId = result.clientId;
                cachedClientId.current = clientId;
                setClientId(result.clientId);
            }
            await handleNextStep(result.nextStep, {
                ...signInParamters,
                ...params,
                clientId,
            });
        }
        finally {
            setIsFetching(null);
        }
        return true;
    }, [reset, fetchSignInThrows, fetchSignIn, handleNextStep]);
    return [
        signIn,
        isFetching,
        alternativeStep,
        handleNextStep,
        nextAlternativeStep,
    ];
};
export default useSignIn;
